import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/Alert/Alert';
import { Button } from '@/components/Button/Button';
import { Sheet } from '@/components/Sheet/Sheet';
import { useUploadAttachment } from '@/features/attachments';
import type { Attachment } from '@/features/attachments';
import { t } from '@/lib/i18n';
import { useCreateAdHocJob } from '../hooks';
import { MemberDropdownPicker } from './MemberDropdownPicker';
import styles from './DescribeJobSheet.module.css';

const FORM_ID = 'describe-job-sheet-form';

interface PendingAttachment {
  localId: string;
  name: string;
  size: number;
  attachment?: Attachment;
  error?: string;
}

export interface DescribeJobSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the member picker is hidden and this member is used. */
  prefillMember?: { id: string; name: string };
}

export function DescribeJobSheet({ open, onOpenChange, prefillMember }: DescribeJobSheetProps) {
  const navigate = useNavigate();
  const create = useCreateAdHocJob();
  const upload = useUploadAttachment();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [memberId, setMemberId] = useState<string | null>(prefillMember?.id ?? null);
  const [instructions, setInstructions] = useState('');
  const [title, setTitle] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset all state when the sheet opens.
  useEffect(() => {
    if (open) {
      setMemberId(prefillMember?.id ?? null);
      setInstructions('');
      setTitle('');
      setPendingAttachments([]);
      setSubmitError(null);
    }
    // prefillMember intentionally omitted: reset only on open/close transitions,
    // not every time the parent re-renders with a new prefill value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasPendingUploads = pendingAttachments.some((p) => !p.attachment && !p.error);
  const attachmentIds = pendingAttachments.filter((p) => p.attachment).map((p) => p.attachment!.id);
  const canSubmit =
    Boolean(memberId) && Boolean(instructions.trim()) && !create.isPending && !hasPendingUploads;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!memberId) {
      setSubmitError(t('Pick a team member before starting this job.'));
      return;
    }
    setSubmitError(null);
    try {
      const job = await create.mutateAsync({
        assigned_member_id: memberId,
        instructions: instructions.trim(),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(attachmentIds.length > 0 ? { attachment_ids: attachmentIds } : {}),
      });
      onOpenChange(false);
      void navigate(`/jobs/${job.id}`);
    } catch {
      setSubmitError(t('Could not start the job. Try again.'));
    }
  }

  function handleFiles(filesList: FileList | null) {
    if (!filesList) return;
    const files = Array.from(filesList);
    const additions: PendingAttachment[] = files.map((f) => ({
      localId: crypto.randomUUID(),
      name: f.name,
      size: f.size,
    }));
    setPendingAttachments((current) => [...current, ...additions]);

    additions.forEach((p, idx) => {
      const file = files[idx];
      if (!file) return;
      upload
        .mutateAsync({ file })
        .then((res) => {
          setPendingAttachments((current) =>
            current.map((c) =>
              c.localId === p.localId ? { ...c, attachment: res.attachment } : c,
            ),
          );
        })
        .catch((err) => {
          setPendingAttachments((current) =>
            current.map((c) =>
              c.localId === p.localId
                ? { ...c, error: err instanceof Error ? err.message : 'Upload failed' }
                : c,
            ),
          );
        });
    });

    // Reset the file input so the same file can be re-added after removal.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function removeAttachment(localId: string) {
    setPendingAttachments((current) => current.filter((p) => p.localId !== localId));
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('Describe a Job')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            disabled={!canSubmit}
            isLoading={create.isPending}
          >
            {t('Run →')}
          </Button>
        </>
      }
    >
      {submitError ? (
        <Alert variant="error" onDismiss={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      ) : null}

      <form id={FORM_ID} className={styles.form} onSubmit={(e) => void handleSubmit(e)}>
        {/* ── For whom ─────────────────────────────────────────────── */}
        {prefillMember ? (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>{t('For')}</div>
            <span className={styles.prefillMember}>{prefillMember.name}</span>
          </div>
        ) : (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>{t('For whom?')}</div>
            <MemberDropdownPicker value={memberId} onChange={setMemberId} />
          </div>
        )}

        {/* ── Instructions ─────────────────────────────────────────── */}
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="describe-instructions">
            {t('What do you need done?')}
            <span className={styles.required} aria-hidden="true">
              {' *'}
            </span>
          </label>
          <textarea
            id="describe-instructions"
            className={styles.textarea}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={8}
            placeholder={t('Describe what you need…')}
            required
          />
        </div>

        {/* ── Title (optional) ─────────────────────────────────────── */}
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="describe-title">
            {t('Title')}
            <span className={styles.optional}>{' (' + t('optional') + ')'}</span>
          </label>
          <input
            id="describe-title"
            type="text"
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder={t('Auto-generated after you run')}
          />
        </div>

        {/* ── File attachments ─────────────────────────────────────── */}
        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('Add Files')}</span>
          <div>
            <label className={styles.attachBtn}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className={styles.fileInput}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {t('Choose files…')}
            </label>
          </div>
          {pendingAttachments.length > 0 ? (
            <ul className={styles.chips}>
              {pendingAttachments.map((p) => (
                <li key={p.localId} className={styles.chip}>
                  <span className={styles.chipName}>{p.name}</span>
                  {p.error ? (
                    <span className={styles.chipError} title={p.error}>
                      {'!'}
                    </span>
                  ) : !p.attachment ? (
                    <span className={styles.chipSpinner} aria-label={t('Uploading')}>
                      {'…'}
                    </span>
                  ) : (
                    <span aria-hidden="true">{'✓'}</span>
                  )}
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => removeAttachment(p.localId)}
                    aria-label={t('Remove') + ' ' + p.name}
                  >
                    {'×'}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </form>
    </Sheet>
  );
}
